import {
  AppstoreFilled,
  HomeFilled,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  NotificationFilled,
} from '@ant-design/icons'
import { Badge, Layout, Menu } from 'antd'
import classNames from 'classnames'
import Link from 'next/link'
import { Router, useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { AnalyticsSubMenu } from './AnalyticsSubMenu'
import { EnvironmentSubMenu } from './EnvironmentsSubMenu'
import { RepositorySubMenu } from './RepositoriesSubMenu'
import { SettingsSubMenu } from './SettingsSubMenu'
import { SiderMenuItem } from './SiderMenuItem'
import { StatusLink } from './StatusLink'
import { WorkspaceMenu } from './WorkspaceMenu'
import { getMenuItem, MenuItem } from '@/components/ui/common/MenuItem'
import uiUrls from '@/config/uiUrls'
import { packageUrls } from '@/config/urls/account/package'
import { usePaywallContext } from '@/context/PaywallContext'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { DEFAULT_PAGINATION } from '@/reducers/store/installedPackagesReducer'
import { useHttpFetcher } from '@/utils/http'
import { buildI18nFunction, IntlFunction } from '@/utils/messages'
import { isFeatureAvailableForWorkspace } from '@/utils/projects'
import { getOpenAndSelectedKeysByPath } from '@/utils/urls'

import {
  AccountPackageStorePageable,
  AccountPackageUpdatesPageable,
  Workspace,
} from '@/types/index.d'

import styles from './SiderMenu.module.css'
import iconStyles from './StatusLink.module.css'

const { Sider } = Layout

export type SiderMenuProps = {
  baseUrl: string
  className: string
}

enum SiderMenuKeys {
  COLLAPSE = 'collapse',
  HOME = 'home',
  UPDATES = 'updates',
  REFERRALS = 'referrals',
  STATUS = 'status',
}

export function SiderMenu({ baseUrl, className }: SiderMenuProps): JSX.Element {
  const { showPaywallModal } = usePaywallContext()

  const t = buildI18nFunction(useIntl().formatMessage)
  const router = useRouter()

  const {
    workspaceId,
    workspace: currentWorkspace,
    membership,
  } = useWorkspaceContext()
  const isFreemium = currentWorkspace.status === 'FREEMIUM'
  const isPoliciesV2Available = isFeatureAvailableForWorkspace(
    'policies_v2',
    currentWorkspace
  )

  const { data: packagesToUpdate } =
    useHttpFetcher<AccountPackageUpdatesPageable>({
      url: isPoliciesV2Available
        ? packageUrls.getWorkspacePackagesUpdates({
            workspaceId,
            // This params are the same of the one used in the
            // installedPackagesReducer. So that eventually this call
            // gets cached and we save on call when visiting /store
            params: {
              page: 0,
              size: DEFAULT_PAGINATION.pageSize,
            },
          })
        : null,
    })

  const { data: packagesToInstall } =
    useHttpFetcher<AccountPackageStorePageable>({
      url:
        !isFreemium && isPoliciesV2Available
          ? packageUrls.getWorkspacePackagesStore({
              workspaceId: currentWorkspace?.id,
              // This params are the same of the one used in the
              // installedPackagesReducer. So that eventually this call
              // gets cached and we save on call when visiting /store
              params: {
                page: 0,
                size: DEFAULT_PAGINATION.pageSize,
              },
            })
          : null,
    })

  const [{ collapsed }, setCollapse] = useLocalStorage<{
    collapsed: boolean
  }>('SiderMenu', '', { collapsed: false })
  const { openKeys, selectedKeys } = getOpenAndSelectedKeysByPath(
    baseUrl,
    collapsed,
    router?.asPath
  )

  const items = buildMenuItems({
    baseUrl,
    collapsed,
    currentWorkspace,
    membership,
    showPaywallModal,
    t,
    totalPackages:
      packagesToUpdate?.content?.length + packagesToInstall?.content?.length,
    router,
  })

  return (
    <Sider
      className={classNames(className, styles['sider'])}
      collapsed={collapsed}
      width={280}
    >
      <WorkspaceMenu collapsed={collapsed} />
      <Layout className={styles['menu-container']}>
        <Menu
          className={styles['menu-root']}
          defaultOpenKeys={openKeys}
          items={items}
          mode="inline"
          onClick={(info) =>
            info?.key === SiderMenuKeys.COLLAPSE &&
            setCollapse({ collapsed: !collapsed })
          }
          selectedKeys={selectedKeys}
        />
      </Layout>
    </Sider>
  )
}

function buildMenuItems({
  baseUrl,
  collapsed,
  currentWorkspace,
  membership,
  showPaywallModal,
  t,
  totalPackages,
  router,
}): MenuItem[] {
  const items: MenuItem[] = [HomeMenuItem(baseUrl, t)]

  if (isFeatureAvailableForWorkspace('git', currentWorkspace)) {
    items.push({
      'data-cy': 'SiderMenu.repositories',
      className: styles['repositories-submenu'],
      ...RepositorySubMenu(currentWorkspace, showPaywallModal),
    })
  }

  if (isFeatureAvailableForWorkspace('environment', currentWorkspace)) {
    items.push({
      'data-cy': 'SiderMenu.environments',
      ...EnvironmentSubMenu(currentWorkspace, showPaywallModal),
    })
  }

  items.push({
    'data-cy': 'SiderMenu.analytics',
    ...AnalyticsSubMenu({
      membership,
      showPaywallModal,
      workspace: currentWorkspace,
    }),
  })

  if (membership.role === 'ADMIN') {
    items.push(
      StoreMenuItem(
        t,
        totalPackages,
        showPaywallModal,
        currentWorkspace,
        router
      )
    )
  }

  items.push({
    className: styles.filler,
    type: 'divider',
  })

  if (isFeatureAvailableForWorkspace('referral', currentWorkspace)) {
    if (currentWorkspace?.status === 'FREEMIUM') {
      items.push({
        'data-cy': 'SiderMenu.referral',
        ...ReferralMenuItem(),
      })
    }
  }

  items.push({ 'data-cy': 'SiderMenu.status', ...StatusMenuItem() })
  if (membership.role === 'ADMIN') {
    items.push({
      'data-cy': 'SiderMenu.settings',
      ...SettingsSubMenu(baseUrl, showPaywallModal, t, 'SiderMenu'),
    })
  }

  items.push({
    type: 'divider',
  })

  items.push({
    'data-cy': 'SiderMenu.collapse',
    style: collapsed ? {} : { flexDirection: 'row-reverse' },
    ...CollapseMenuItem(collapsed),
  })
  return items
}

function StatusMenuItem(): MenuItem {
  const statusLink = <StatusLink />

  return (
    statusLink?.type() !== null &&
    getMenuItem('', SiderMenuKeys.STATUS, statusLink)
  )
}

function HomeMenuItem(baseUrl: string, t: IntlFunction): MenuItem {
  return getMenuItem(
    <Link href={`${baseUrl}`}>
      <span className="menu-item-title">{t('SiderMenu.home')}</span>
    </Link>,
    SiderMenuKeys.HOME,
    <HomeFilled />
  )
}

function ReferralMenuItem(): MenuItem {
  const t = buildI18nFunction(useIntl().formatMessage)
  return getMenuItem(
    <Link href={`/settings/referrals`}>
      <span className="menu-item-title">
        {t('SiderMenu.settings.referrals.FREEMIUM')}
      </span>
    </Link>,
    SiderMenuKeys.REFERRALS,
    <NotificationFilled />
  )
}

const CollapseMenuItem = (collapsed) => {
  return getMenuItem(
    null,
    SiderMenuKeys.COLLAPSE,
    renderCollapsedButtonIcon(collapsed)
  )
}

export function renderCollapsedButtonIcon(collapsed?: boolean): JSX.Element {
  return collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
}

function StoreMenuItem(
  t: IntlFunction,
  totalPackages: number,
  showPaywallModal: (id: string) => void | null,
  currentWorkspace: Workspace,
  router: Router
): MenuItem & { ['data-cy']: string } {
  return SiderMenuItem({
    feature: 'packages_store',
    href: uiUrls.settings.store,
    key: 'store',
    showPaywallModal,
    t,
    workspace: currentWorkspace,
    icon: (
      <Badge dot={totalPackages > 0} className={iconStyles['icon-badge']}>
        <AppstoreFilled />
      </Badge>
    ),
    router,
  })
}
